import { getErrorCode, getErrorMessageOrDefault, unknownToError, } from "@vortex/shared";
import * as path from "path";
import { sync as writeAtomicSync } from "write-file-atomic";
import { showDialog } from "../../../actions/notifications";
import { ProcessCanceled, UserCanceled } from "../../../util/CustomErrors";
import * as fs from "../../../util/fs";
import { writeFileAtomic } from "../../../util/fsAtomic";
import getVortexPath from "../../../util/getVortexPath";
import { log } from "../../../util/log";
import { activeGameId, discoveryByGame, installPathForGame, } from "../../../util/selectors";
import { getSafe } from "../../../util/storeHelper";
import { deBOM, makeQueue, truthy } from "../../../util/util";
import { getGame } from "../../gamemode_management/util/getGame";
import { getActivator, getCurrentActivator } from "./deploymentMethods";
import format_1 from "./manifest_formats/format_1";
const CURRENT_VERSION = 1;
const formats = {
    1: format_1,
};
function emptyManifest(instance) {
    return {
        version: CURRENT_VERSION,
        instance,
        files: [],
    };
}
/**
 * since the manifest is read from disc, it could have been modified by the user.
 * Check it for correctness
 */
function repairManifest(input) {
    if (!truthy(input.version)) {
        input.version = CURRENT_VERSION;
    }
    if (!truthy(input.instance)) {
        input.instance = "";
    }
    input.files = input.files.reduce((prev, file) => {
        if (file !== null &&
            file.relPath !== undefined &&
            file.relPath !== null &&
            file.source !== undefined &&
            file.source !== null &&
            file.time !== undefined &&
            file.time !== null) {
            prev.push(file);
        }
        return prev;
    }, []);
    return input;
}
function readManifest(data) {
    if (data === "") {
        return undefined;
    }
    const msgpack = require("@msgpack/msgpack");
    let parsed;
    try {
        parsed =
            typeof data === "string" ? JSON.parse(deBOM(data)) : msgpack.decode(data);
    }
    catch (err) {
        const newErr = new Error(`Failed to parse manifest: "${getErrorMessageOrDefault(err)}"`);
        // invalid input data, not a bug
        newErr["allowReport"] = false;
        throw newErr;
    }
    let lastVersion = 0;
    while (lastVersion < CURRENT_VERSION) {
        parsed = formats[parsed.version || 1](parsed);
        if (parsed.version === lastVersion && parsed.version < CURRENT_VERSION) {
            // this should not happen!
            throw new Error(`unsupported format upgrade ${parsed.version} -> ${CURRENT_VERSION}`);
        }
        lastVersion = parsed.version;
    }
    if (parsed.files === undefined) {
        parsed.files = [];
    }
    return repairManifest(parsed);
}
export function purgeDeployedFiles(basePath, files) {
    return Promise.all(files.map((file) => {
        const fullPath = path.join(basePath, file.relPath);
        return fs
            .statAsync(fullPath)
            .then((stats) => {
            // the timestamp from stat has ms precision but the one from the manifest doesn't
            return stats.mtime.getTime() - file.time < 1000
                ? fs.unlinkAsync(fullPath)
                : Promise.resolve();
        })
            .catch((err) => {
            if (getErrorCode(err) !== "ENOENT") {
                return Promise.reject(err);
            } // otherwise ignore
        });
    })).then(() => undefined);
}
function queryPurgeTextSafe(t) {
    return t("IMPORTANT: This game was modded by another instance of Vortex.\n\n" +
        "If you switch between different instances (or between shared and " +
        "single-user mode) it's better if you purge mods before switching.\n\n" +
        "Vortex can try to clean up now but this is less reliable (*) than doing it " +
        "from the instance that deployed the files in the first place.\n\n" +
        "If you modified any files in the game directory you should back them up " +
        "before continuing.\n\n" +
        "(*) This purge relies on a manifest of deployed files, created by that other " +
        "instance. Files that have been changed since that manifest was created " +
        "won't be removed to prevent data loss. If the manifest is damaged or " +
        'outdated the purge may be incomplete. When purging from the "right" instance ' +
        "the manifest isn't required, it can reliably deduce which files need to " +
        "be removed.");
}
function queryPurgeTextUnsafe(t) {
    return t("IMPORTANT: This game was modded by another instance of Vortex.\n\n" +
        "Vortex can only proceed by purging the mods from that other instance.\n\n" +
        "This will irreversibly **destroy** the mod installations from that other " +
        "instance!\n\n" +
        "You should instead cancel now, open that other vortex instance and purge " +
        "from there. This can also be caused by switching between shared and " +
        "single-user mode.");
}
function queryPurge(api, basePath, files, safe) {
    const t = api.translate;
    const text = safe ? queryPurgeTextSafe(t) : queryPurgeTextUnsafe(t);
    return Promise.resolve(api.store
        .dispatch(showDialog("info", t("Purge files from different instance?"), {
        text,
    }, [{ label: "Cancel" }, { label: "Purge" }])))
        .then((result) => {
        if (result.action === "Purge") {
            return purgeDeployedFiles(basePath, files).catch((err) => {
                api.showErrorNotification("Purging failed", err, {
                    allowReport: false,
                });
                return Promise.reject(new UserCanceled());
            });
        }
        else {
            return Promise.reject(new UserCanceled());
        }
    });
}
function readManifestFile(filePath) {
    return Promise.resolve(fs.readFileAsync(filePath, "utf8")).then((data) => readManifest(data));
}
function readManifestFileBinary(filePath) {
    return Promise.resolve(fs.readFileAsync(filePath)).then((data) => readManifest(data));
}
function getManifestImpl(api, instanceId, filePath, backupPath, backup2Path) {
    return readManifestFile(filePath)
        .catch((err) => {
        if (err instanceof UserCanceled) {
            return Promise.reject(err);
        }
        const errObj = unknownToError(err);
        const code = getErrorCode(err);
        if (code === "ENOENT") {
            return emptyManifest(instanceId);
        }
        if (code === "EPERM") {
            errObj.message =
                `The manifest file "${filePath}" is inaccessible due to ` +
                    "insufficient permissions.\nPlease ensure your Windows user account " +
                    "has full read/write permissions to the manifest file and try again.";
            errObj["allowReport"] = false;
            return Promise.reject(errObj);
        }
        if (errObj.message.startsWith("Unexpected token") ||
            errObj.message.startsWith("Unexpected end of JSON input")) {
            errObj.message =
                `The manifest file "${filePath}" is corrupted.\n` +
                    'You should delete it, then immediately click the "Purge" button ' +
                    'on the "Mods" page, then deploy again.';
        }
        return readManifestFileBinary(backup2Path)
            .catch((inner) => {
            if (getErrorCode(inner) === "ENOENT") {
                return readManifestFile(backupPath);
            }
            throw inner;
        })
            .then((data) => api
            .showDialog("question", "Manifest damaged", {
            text: "The deployment manifest has been corrupted.\n" +
                "Fortunately we have a backup that seems to be intact.",
            parameters: {
                filePath,
            },
        }, [{ label: "Cancel" }, { label: "Restore from backup" }])
            .then((result) => {
            if (result.action === "Cancel") {
                errObj["allowReport"] = false;
                return Promise.reject(errObj);
            }
            else {
                return Promise.resolve(data);
            }
        }))
            .catch((backupErr) => {
            errObj.message += "\nBackup couldn't be read: " + unknownToError(backupErr).message;
            return Promise.reject(errObj);
        });
    })
        .then((manifest) => manifest !== undefined ? manifest : emptyManifest(instanceId));
}
export function fallbackPurgeType(api, activator, gameId, modType, deployPath, stagingPath) {
    const state = api.store.getState();
    const typeTag = modType !== undefined && modType.length > 0 ? modType + "." : "";
    const tagFileName = `vortex.deployment.${typeTag}json`;
    const tagFilePath = path.join(deployPath, tagFileName);
    const tagBackupPath = path.join(stagingPath, tagFileName);
    const tagBackup2Path = path.join(stagingPath, `vortex.deployment.backup.${typeTag}msgpack`);
    const instanceId = state.app.instanceId;
    return getManifestImpl(api, instanceId, tagFilePath, tagBackupPath, tagBackup2Path)
        .then((tagObject) => {
        let result;
        if (tagObject.files.length > 0) {
            let safe = true;
            if (tagObject.deploymentMethod !== undefined) {
                const previousActivator = getActivator(tagObject.deploymentMethod);
                if (previousActivator !== undefined &&
                    !previousActivator.isFallbackPurgeSafe) {
                    safe = false;
                }
            }
            result = purgeDeployedFiles(deployPath, tagObject.files)
                .then(() => saveActivation(gameId, modType, state.app.instanceId, deployPath, stagingPath, [], activator !== undefined ? activator.id : undefined))
                .then(() => Promise.resolve());
        }
        else {
            result = Promise.resolve();
        }
        return result;
    })
        .catch((err) => Promise.reject(err));
}
/**
 * purge files using information from the manifest
 */
export async function fallbackPurge(api, gameId) {
    const state = api.store.getState();
    if (gameId === undefined) {
        gameId = activeGameId(state);
    }
    const gameDiscovery = discoveryByGame(state, gameId);
    const game = getGame(gameId);
    if (game === undefined || gameDiscovery?.path === undefined) {
        return Promise.reject(new ProcessCanceled("game got disabled"));
    }
    const modPaths = game.getModPaths(gameDiscovery.path);
    const stagingPath = installPathForGame(state, gameId);
    const activator = getCurrentActivator(state, gameId, false);
    for (const typeId of Object.keys(modPaths)) {
        await fallbackPurgeType(api, activator, gameId, typeId, modPaths[typeId], stagingPath);
    }
}
const activationQueue = makeQueue();
export function withActivationLock(func, tryOnly = false) {
    return activationQueue(func, tryOnly);
}
/**
 * return a manifest (detailing which files are currently deployed by Vortex)
 * Please note that the manifest is intended only as kind of a fallback, core functionality
 * of Vortex is designed to work cleanly even if the manifest is deleted by the user and
 * the same should be true for any extension using this function: Work on the assumption
 * that the manifest may be missing or outdated.
 * @remarks
 * This call is expensive as it attempts to read the manifest every time. Store the
 * result or call infrequently to minimise allocations and/or lag.
 * @param api api
 * @param modType the mod type for which to retrieve the manifest, default mod type if undefined
 * @param gameId the game for which to retrieve the manifest, defaults to the current game.
 */
export function getManifest(api, modType, gameId) {
    const state = api.store.getState();
    const instanceId = state.app.instanceId;
    try {
        if (gameId === undefined) {
            gameId = activeGameId(state);
        }
        if (modType === undefined) {
            modType = "";
        }
        const game = getGame(gameId);
        const discovery = getSafe(state, ["settings", "gameMode", "discovered", gameId], undefined);
        if (discovery?.path === undefined || game === undefined) {
            return Promise.resolve(emptyManifest(instanceId));
        }
        const stagingPath = installPathForGame(state, gameId);
        const deployPath = game.getModPaths(discovery.path)[modType];
        if (stagingPath === undefined || deployPath === undefined) {
            return Promise.resolve(emptyManifest(instanceId));
        }
        const typeTag = modType !== undefined && modType.length > 0 ? modType + "." : "";
        const tagFileName = `vortex.deployment.${typeTag}json`;
        const tagFilePath = path.join(deployPath, tagFileName);
        const tagBackupPath = path.join(stagingPath, tagFileName);
        const tagBackup2Path = path.join(stagingPath, `vortex.deployment.${typeTag}msgpack`);
        return getManifestImpl(api, instanceId, tagFilePath, tagBackupPath, tagBackup2Path);
    }
    catch (err) {
        return Promise.reject(err);
    }
}
export function loadActivation(api, gameId, modType, deployPath, stagingPath, activator) {
    if (deployPath === undefined) {
        return Promise.resolve([]);
    }
    const typeTag = modType !== undefined && modType.length > 0 ? modType + "." : "";
    const tagFileName = `vortex.deployment.${typeTag}json`;
    const tagFilePath = path.join(deployPath, tagFileName);
    const tagBackupPath = path.join(stagingPath, tagFileName);
    const tagBackup2Path = path.join(stagingPath, `vortex.deployment.${typeTag}msgpack`);
    const state = api.store.getState();
    const instanceId = state.app.instanceId;
    return getManifestImpl(api, instanceId, tagFilePath, tagBackupPath, tagBackup2Path).then((tagObject) => {
        let result;
        if (tagObject.instance !== instanceId && tagObject.files.length > 0) {
            let safe = true;
            if (tagObject.deploymentMethod !== undefined) {
                const previousActivator = getActivator(tagObject.deploymentMethod);
                if (previousActivator !== undefined &&
                    !previousActivator.isFallbackPurgeSafe) {
                    safe = false;
                }
            }
            result = queryPurge(api, deployPath, tagObject.files, safe)
                .then(() => saveActivation(gameId, modType, state.app.instanceId, deployPath, stagingPath, [], activator.id))
                .then(() => Promise.resolve([]));
        }
        else {
            result = Promise.resolve(tagObject.files);
        }
        return result;
    });
}
export function saveActivation(gameId, modType, instance, gamePath, stagingPath, activation, activatorId) {
    const typeTag = modType !== undefined && modType.length > 0 ? modType + "." : "";
    const dataRaw = {
        instance,
        version: CURRENT_VERSION,
        deploymentMethod: activatorId,
        gameId,
        deploymentTime: Date.now(),
        stagingPath,
        targetPath: gamePath,
        files: activation,
    };
    const dataJSON = JSON.stringify(dataRaw, undefined, 2);
    try {
        JSON.parse(dataJSON);
    }
    catch (err) {
        const failedPath = path.join(getVortexPath("temp"), "failed_manifest.json");
        fs.writeFileSync(failedPath, dataJSON, { encoding: "utf8" });
        const repErr = new Error(`failed to serialize deployment information: "${getErrorMessageOrDefault(err)}"`);
        repErr["attachFilesOnReport"] = [failedPath];
        return Promise.reject(repErr);
    }
    const tagFileName = `vortex.deployment.${typeTag}json`;
    const tagFilePath = path.join(gamePath, tagFileName);
    const tagBackupPath = path.join(stagingPath, `vortex.deployment.${typeTag}msgpack`);
    if (activation.length > 0) {
        // write backup synchronously
        try {
            const msgpack = require("@msgpack/msgpack");
            writeAtomicSync(tagBackupPath, Buffer.from(msgpack.encode(dataRaw)));
        }
        catch (err) {
            log("error", "Failed to write manifest backup", getErrorMessageOrDefault(err));
        }
    }
    else {
        try {
            fs.removeSync(tagBackupPath);
        }
        catch (err) {
            const code = getErrorCode(err);
            if (code !== "ENOENT") {
                log("error", "failed to remove manifest backup", getErrorMessageOrDefault(err));
            }
        }
    }
    return activation.length === 0
        ? fs.removeAsync(tagFilePath).catch(() => undefined)
        : writeFileAtomic(tagFilePath, dataJSON)
            .then(() => fs
            .removeAsync(path.join(stagingPath, tagFileName))
            .catch((err) => {
            if (getErrorCode(err) === "ENOENT") {
                return null;
            }
            throw err;
        }));
}
